// ============================================================================
// useTeamMemberProfile.js — Supabase CRUD for Team Member Fullscreen Page
// ============================================================================
// WHERE TO PLACE: src/hooks/useTeamMemberProfile.js
// ============================================================================
// Provides hooks for:
//   - user_profiles              (personal details, IDs, job info, gap fields)
//   - user_addresses             (multiple addresses per user)
//   - user_salary_history        (salary revisions — read-only in UI)
//   - user_employers             (past employers per user)
//   - user_education             (degrees per user)
//   - user_performance_reviews   (dated review entries)
//   - user_evaluation_reports    (supervisor evaluation entries)
//   - user_notes                 (v6 — free-form notes incl. hiring interview)
//   - address_directory          (global address autocomplete)
//
// Conventions:
//   DB: snake_case  |  UI: camelCase
//   Normalize on read, denormalize on write (same as useHFBData.js)
//   All writes go through requireSession() for RLS compliance
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatSupabaseError, syncTeamPortraitFolder } from './useSupabase';

// ============================================================================
// DEBUG FLAG
// ============================================================================
const DEBUG_PROFILE = false;

function debugLog(...args) {
  if (DEBUG_PROFILE) console.log('[useTeamMemberProfile]', ...args);
}

// ============================================================================
// SESSION GUARD (mirrors useSupabase.js)
// ============================================================================
const requireSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw new Error('Authentication error: ' + error.message);
  if (!session?.user) throw new Error('Please sign in to perform this action.');
  return session.user.id;
};

// ============================================================================
// NORMALIZERS: snake_case → camelCase
// ============================================================================

function normalizeProfile(row) {
  if (!row) return null;
  return {
    id: row.id,
    userId: row.user_id,
    // Contacts
    phoneMobile: row.phone_mobile || '',
    phoneWhatsapp: row.phone_whatsapp || '',
    phoneCustomLabel: row.phone_custom_label || '',
    phoneCustomValue: row.phone_custom_value || '',
    emailOffice: row.email_office || '',
    emailPersonal: row.email_personal || '',
    // Personal
    dob: row.dob || null,
    panCard: row.pan_card || '',
    graduationDate: row.graduation_date || null,
    designation: row.designation || '',
    personalityTestResult: row.personality_test_result || '',
    personalityTestLink: row.personality_test_link || '',
    expertise: row.expertise || [],
    profilePhotoOriginal: row.profile_photo_original || '',
    profilePhotoRect: row.profile_photo_rect || '',
    profilePhotoRound: row.profile_photo_round || '',
    profileIntro: row.profile_intro || '',
    // Job
    currentJobRole: row.current_job_role || '',
    currentJobLevel: row.current_job_level ?? null,
    officeLocation: row.office_location || 'NL',
    joiningDate: row.joining_date || null,
    leavingDate: row.leaving_date || null,
    // Gap fields (fix #5)
    experienceGapYears: row.experience_gap_years ?? null,
    experienceGapNotes: row.experience_gap_notes || '',
    // Hiring / CV snapshot (v2)
    homeCity: row.home_city || '',
    portfolioUrl: row.portfolio_url || '',
    pastEmployment: row.past_employment || [],
    yearsExperience: row.years_experience ?? null,
    noticePeriodEndDate: row.notice_period_end_date || null,
    softwareProficiency: row.software_proficiency || {},
    salaryExpectationsMonthly: row.salary_expectations_monthly ?? null,
    currentSalaryMonthly: row.current_salary_monthly ?? null,
    futurePlans: row.future_plans || '',
    hiringOverallComments: row.hiring_overall_comments || '',
    hiringFinalVerdictNotes: row.hiring_final_verdict_notes || '',
    hiringCandidateId: row.hiring_candidate_id || null,
    // Planning
    planningMasters: row.planning_masters || '',
    // Timestamps
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function denormalizeProfile(data) {
  const db = {};
  if (data.userId !== undefined)             db.user_id = data.userId;
  if (data.phoneMobile !== undefined)        db.phone_mobile = data.phoneMobile || null;
  if (data.phoneWhatsapp !== undefined)      db.phone_whatsapp = data.phoneWhatsapp || null;
  if (data.phoneCustomLabel !== undefined)   db.phone_custom_label = data.phoneCustomLabel || null;
  if (data.phoneCustomValue !== undefined)   db.phone_custom_value = data.phoneCustomValue || null;
  if (data.emailOffice !== undefined)        db.email_office = data.emailOffice || null;
  if (data.emailPersonal !== undefined)      db.email_personal = data.emailPersonal || null;
  if (data.dob !== undefined)                db.dob = data.dob || null;
  if (data.panCard !== undefined)            db.pan_card = data.panCard || null;
  if (data.graduationDate !== undefined)     db.graduation_date = data.graduationDate || null;
  if (data.designation !== undefined)        db.designation = data.designation || null;
  if (data.personalityTestResult !== undefined) db.personality_test_result = data.personalityTestResult || null;
  if (data.personalityTestLink !== undefined)   db.personality_test_link = data.personalityTestLink || null;
  if (data.expertise !== undefined)             db.expertise = Array.isArray(data.expertise) ? data.expertise : [];
  if (data.profilePhotoOriginal !== undefined)  db.profile_photo_original = data.profilePhotoOriginal || null;
  if (data.profilePhotoRect !== undefined)      db.profile_photo_rect = data.profilePhotoRect || null;
  if (data.profilePhotoRound !== undefined)     db.profile_photo_round = data.profilePhotoRound || null;
  if (data.profileIntro !== undefined)           db.profile_intro = data.profileIntro || null;
  // NOTE: currentJobRole and currentJobLevel are READ-ONLY snapshot fields
  // managed exclusively by the user_role_history trigger. Do NOT write them here.
  // if (data.currentJobRole !== undefined)     db.current_job_role = data.currentJobRole || null;
  // if (data.currentJobLevel !== undefined)    db.current_job_level = data.currentJobLevel ?? null;
  if (data.officeLocation !== undefined)     db.office_location = data.officeLocation || 'NL';
  if (data.joiningDate !== undefined)        db.joining_date = data.joiningDate || null;
  if (data.leavingDate !== undefined)        db.leaving_date = data.leavingDate || null;
  // Gap fields
  if (data.experienceGapYears !== undefined) db.experience_gap_years = data.experienceGapYears ?? null;
  if (data.experienceGapNotes !== undefined) db.experience_gap_notes = data.experienceGapNotes || null;
  // Hiring / CV snapshot (v2)
  if (data.homeCity !== undefined)                 db.home_city = data.homeCity || null;
  if (data.portfolioUrl !== undefined)             db.portfolio_url = data.portfolioUrl || null;
  if (data.pastEmployment !== undefined)           db.past_employment = data.pastEmployment || [];
  if (data.yearsExperience !== undefined)          db.years_experience = data.yearsExperience ?? null;
  if (data.noticePeriodEndDate !== undefined)      db.notice_period_end_date = data.noticePeriodEndDate || null;
  if (data.softwareProficiency !== undefined)      db.software_proficiency = data.softwareProficiency || {};
  if (data.salaryExpectationsMonthly !== undefined) db.salary_expectations_monthly = data.salaryExpectationsMonthly ?? null;
  if (data.currentSalaryMonthly !== undefined)     db.current_salary_monthly = data.currentSalaryMonthly ?? null;
  if (data.futurePlans !== undefined)              db.future_plans = data.futurePlans || null;
  if (data.hiringOverallComments !== undefined)    db.hiring_overall_comments = data.hiringOverallComments || null;
  if (data.hiringFinalVerdictNotes !== undefined)  db.hiring_final_verdict_notes = data.hiringFinalVerdictNotes || null;
  if (data.hiringCandidateId !== undefined)        db.hiring_candidate_id = data.hiringCandidateId || null;
  if (data.planningMasters !== undefined)          db.planning_masters = data.planningMasters || null;
  return db;
}

function normalizeAddress(row) {
  return {
    id: row.id, userId: row.user_id,
    label: row.label || '', line1: row.line1 || '', line2: row.line2 || '',
    city: row.city || '', state: row.state || '',
    postalCode: row.postal_code || '', country: row.country || '',
  };
}

function denormalizeAddress(data) {
  const db = {};
  if (data.userId !== undefined)     db.user_id = data.userId;
  if (data.label !== undefined)      db.label = data.label || null;
  if (data.line1 !== undefined)      db.line1 = data.line1 || null;
  if (data.line2 !== undefined)      db.line2 = data.line2 || null;
  if (data.city !== undefined)       db.city = data.city || null;
  if (data.state !== undefined)      db.state = data.state || null;
  if (data.postalCode !== undefined) db.postal_code = data.postalCode || null;
  if (data.country !== undefined)    db.country = data.country || null;
  return db;
}

function normalizeSalaryHistory(row) {
  return {
    id: row.id, userId: row.user_id,
    effectiveDate: row.effective_date, salary: row.salary,
    jobRole: row.job_role || '', jobLevel: row.job_level ?? null,
    reason: row.reason || '', changedBy: row.changed_by || '',
  };
}

function normalizeEducation(row) {
  return {
    id: row.id, userId: row.user_id,
    type: row.type || '', institution: row.institution || '',
    graduationDate: row.graduation_date || null,
    duration: row.duration || '', notes: row.notes || '',
  };
}

function denormalizeEducation(data) {
  const db = {};
  if (data.userId !== undefined)         db.user_id = data.userId;
  if (data.type !== undefined)           db.type = data.type || null;
  if (data.institution !== undefined)    db.institution = data.institution || null;
  if (data.graduationDate !== undefined) db.graduation_date = data.graduationDate || null;
  if (data.duration !== undefined)       db.duration = data.duration || null;
  if (data.notes !== undefined)          db.notes = data.notes || null;
  return db;
}

function normalizeEmployer(row) {
  return {
    id: row.id, userId: row.user_id,
    name: row.name || '', startDate: row.start_date || null,
    endDate: row.end_date || null, role: row.role || '', notes: row.notes || '',
  };
}

function denormalizeEmployer(data) {
  const db = {};
  if (data.userId !== undefined)    db.user_id = data.userId;
  if (data.name !== undefined)      db.name = data.name || null;
  if (data.startDate !== undefined) db.start_date = data.startDate || null;
  if (data.endDate !== undefined)   db.end_date = data.endDate || null;
  if (data.role !== undefined)      db.role = data.role || null;
  if (data.notes !== undefined)     db.notes = data.notes || null;
  return db;
}

function normalizePerformanceReview(row) {
  return {
    id: row.id, userId: row.user_id,
    reviewDate: row.review_date || null,
    year: row.year ?? null,
    reviewType: row.review_type || '',
    overallRating: row.overall_rating ?? null,
    confidence: row.confidence || '',
    criteria: row.criteria || null,
    discussionPoints: row.discussion_points || '',
    problemSolving: row.problem_solving || null,
    futureCriteria: row.future_criteria || '',
    sectionC: row.section_c || {},
    sectionD: row.section_d || {},
    sectionE: row.section_e || {},
    sectionF: row.section_f || {},
    createdAt: row.created_at,
  };
}

function denormalizePerformanceReview(data) {
  const db = {};
  if (data.userId !== undefined)           db.user_id = data.userId;
  if (data.reviewDate !== undefined)       db.review_date = data.reviewDate || null;
  if (data.year !== undefined)             db.year = data.year ?? null;
  if (data.reviewType !== undefined)       db.review_type = data.reviewType || null;
  if (data.overallRating !== undefined)    db.overall_rating = data.overallRating;
  if (data.confidence !== undefined)       db.confidence = data.confidence || null;
  if (data.criteria !== undefined)         db.criteria = data.criteria || null;
  if (data.discussionPoints !== undefined) db.discussion_points = data.discussionPoints || null;
  if (data.problemSolving !== undefined)   db.problem_solving = data.problemSolving || null;
  if (data.futureCriteria !== undefined)   db.future_criteria = data.futureCriteria || null;
  if (data.sectionC !== undefined)         db.section_c = data.sectionC || {};
  if (data.sectionD !== undefined)         db.section_d = data.sectionD || {};
  if (data.sectionE !== undefined)         db.section_e = data.sectionE || {};
  if (data.sectionF !== undefined)         db.section_f = data.sectionF || {};
  return db;
}

function normalizeCriterion(row) {
  return {
    id: row.id,
    reviewId: row.review_id,
    criterionName: row.criterion_name || '',
    score: row.score ?? null,
    strengthsEvidence: row.strengths_evidence || '',
    improvementNotes: row.improvement_notes || '',
    isCustom: row.is_custom ?? false,
    sortOrder: row.sort_order ?? 0,
  };
}

function normalizeAgreement(row) {
  return {
    id: row.id,
    reviewId: row.review_id,
    agreementText: row.agreement_text || '',
    owner: row.owner || '',
    startDate: row.start_date || '',
    dueDate: row.due_date || '',
    successMeasure: row.success_measure || '',
    checkInDate: row.check_in_date || '',
    status: row.status || 'Not started',
    notes: row.notes || '',
    sortOrder: row.sort_order ?? 0,
  };
}

function normalizeEvaluationReport(row) {
  return {
    id: row.id, userId: row.user_id,
    evaluationDate: row.evaluation_date || null,
    year: row.year ?? null,
    criteria: row.criteria || null,
    performanceRating: row.performance_rating ?? null,
    averageRating: row.average_rating ?? null,
    notes: row.notes || '',
    reviewerName: row.reviewer_name || null,
    createdAt: row.created_at,
  };
}

function denormalizeEvaluationReport(data) {
  const db = {};
  if (data.userId !== undefined)            db.user_id = data.userId;
  if (data.evaluationDate !== undefined)    db.evaluation_date = data.evaluationDate || null;
  if (data.year !== undefined)              db.year = data.year ?? null;
  if (data.criteria !== undefined)          db.criteria = data.criteria || null;
  if (data.performanceRating !== undefined) db.performance_rating = data.performanceRating ?? null;
  if (data.averageRating !== undefined)     db.average_rating = data.averageRating ?? null;
  if (data.notes !== undefined)             db.notes = data.notes || null;
  if (data.reviewerName !== undefined)      db.reviewer_name = data.reviewerName || null;
  return db;
}

function normalizeNote(row) {
  return {
    id: row.id,
    userId: row.user_id,
    noteType: row.note_type || '',
    note: row.note || '',
    createdAt: row.created_at,
  };
}

// ============================================================================
// HOOK
// ============================================================================
export function useTeamMemberProfile(userId) {
  const [profile, setProfile] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [education, setEducation] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [performanceReviews, setPerformanceReviews] = useState([]);
  const [evaluationReports, setEvaluationReports] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── FETCH ALL ───────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    debugLog('fetchAll for userId:', userId);
    try {
      const [profileRes, addressRes, salaryRes, eduRes, empRes, reviewRes, evalRes, critRes, agreeRes, notesRes] = await Promise.all([
        supabase.from('user_profiles').select('*').eq('user_id', userId).maybeSingle(),
        supabase.from('user_addresses').select('*').eq('user_id', userId).order('label'),
        supabase.from('user_salary_history').select('*').eq('user_id', userId).order('effective_date', { ascending: false }),
        supabase.from('user_education').select('*').eq('user_id', userId).order('graduation_date', { ascending: false }),
        supabase.from('user_employers').select('*').eq('user_id', userId).order('start_date', { ascending: false }),
        supabase.from('user_performance_reviews').select('*').eq('user_id', userId).order('review_date', { ascending: false }),
        supabase.from('user_evaluation_reports').select('*').eq('user_id', userId).order('evaluation_date', { ascending: false }),
        supabase.from('user_review_criteria').select('*').order('sort_order', { ascending: true }),
        supabase.from('user_review_agreements').select('*').order('sort_order', { ascending: true }),
        supabase.from('user_notes').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
      ]);
      if (profileRes.error && profileRes.error.code !== 'PGRST116') debugLog('Profile fetch error:', profileRes.error);
      setProfile(profileRes.data ? normalizeProfile(profileRes.data) : null);
      if (addressRes.error) debugLog('Address fetch error:', addressRes.error);
      setAddresses((addressRes.data || []).map(normalizeAddress));
      if (salaryRes.error) debugLog('Salary fetch error:', salaryRes.error);
      setSalaryHistory((salaryRes.data || []).map(normalizeSalaryHistory));
      if (eduRes.error) debugLog('Education fetch error:', eduRes.error);
      setEducation((eduRes.data || []).map(normalizeEducation));
      if (empRes.error) debugLog('Employer fetch error:', empRes.error);
      setEmployers((empRes.data || []).map(normalizeEmployer));
      if (reviewRes.error) debugLog('Review fetch error:', reviewRes.error);
      setPerformanceReviews((reviewRes.data || []).map(normalizePerformanceReview));
      if (evalRes.error) debugLog('Eval fetch error:', evalRes.error);
      setEvaluationReports((evalRes.data || []).map(normalizeEvaluationReport));
      // Review criteria & agreements (filter by review ids for this user)
      const reviewIds = (reviewRes.data || []).map(r => r.id);
      setReviewCriteria((critRes.data || []).filter(c => reviewIds.includes(c.review_id)).map(normalizeCriterion));
      setReviewAgreements((agreeRes.data || []).filter(a => reviewIds.includes(a.review_id)).map(normalizeAgreement));
      // Notes (v6)
      if (notesRes.error) debugLog('Notes fetch error:', notesRes.error);
      setNotes((notesRes.data || []).map(normalizeNote));
    } catch (err) {
      console.error('[useTeamMemberProfile] fetchAll error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── PROFILE upsert ──────────────────────────────────────────────────
  const upsertProfile = async (data) => {
    try {
      await requireSession();
      const dbData = { ...denormalizeProfile(data), user_id: userId };
      const { data: result, error: err } = await supabase
        .from('user_profiles').upsert(dbData, { onConflict: 'user_id' }).select().single();
      if (err) throw err;
      setProfile(normalizeProfile(result));

      // A new portrait belongs in the member's Dropbox folder too, with the one
      // it replaces filed into Originals/. Only the portrait variants trigger
      // it — saving a phone number should not push photos anywhere — and it is
      // never awaited, so the profile save neither waits for Dropbox nor fails
      // with it. See syncTeamPortraitFolder for why that is safe.
      if (data.profilePhotoRect !== undefined || data.profilePhotoOriginal !== undefined) {
        syncTeamPortraitFolder(userId);
      }

      return { success: true, data: result };
    } catch (err) {
      console.error('[useTeamMemberProfile] upsertProfile error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'save', entity: 'profile' }) };
    }
  };

  // ── ADDRESS CRUD ────────────────────────────────────────────────────
  const createAddress = async (data) => {
    try {
      await requireSession();
      const dbData = { ...denormalizeAddress(data), user_id: userId };
      const { data: result, error: err } = await supabase.from('user_addresses').insert([dbData]).select().single();
      if (err) throw err;
      setAddresses(prev => [...prev, normalizeAddress(result)]);
      return { success: true, data: result };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'address' }) }; }
  };
  const updateAddress = async (id, data) => {
    try {
      await requireSession();
      const { data: result, error: err } = await supabase.from('user_addresses').update(denormalizeAddress(data)).eq('id', id).select().single();
      if (err) throw err;
      setAddresses(prev => prev.map(a => a.id === id ? normalizeAddress(result) : a));
      return { success: true, data: result };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'address' }) }; }
  };
  const deleteAddress = async (id) => {
    try {
      await requireSession();
      const { error: err } = await supabase.from('user_addresses').delete().eq('id', id);
      if (err) throw err;
      setAddresses(prev => prev.filter(a => a.id !== id));
      return { success: true };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'address' }) }; }
  };

  // ── ADDRESS DIRECTORY SEARCH (fix #2) ───────────────────────────────
  const searchAddressDirectory = async (query) => {
    if (!query || query.length < 2) return [];
    try {
      const { data, error: err } = await supabase
        .from('address_directory')
        .select('*')
        .or(`label.ilike.%${query}%,line1.ilike.%${query}%,city.ilike.%${query}%,country.ilike.%${query}%`)
        .limit(10);
      if (err) { debugLog('Address directory search error:', err); return []; }
      return (data || []).map(r => ({
        id: r.id, label: r.label || '', line1: r.line1 || '', line2: r.line2 || '',
        city: r.city || '', state: r.state || '', postalCode: r.postal_code || '', country: r.country || '',
      }));
    } catch { return []; }
  };

  // ── SALARY HISTORY (read-only — UI prevents edits per fix #7) ──────
  const createSalaryEntry = async () => ({ success: false, error: 'Salary history is read-only; will be sourced from audit trail.' });
  const updateSalaryEntry = async () => ({ success: false, error: 'Salary history is read-only' });
  const deleteSalaryEntry = async () => ({ success: false, error: 'Salary history is read-only' });

  // ── EDUCATION CRUD ──────────────────────────────────────────────────
  const createEducation = async (data) => {
    try {
      await requireSession();
      const dbData = { ...denormalizeEducation(data), user_id: userId };
      const { data: result, error: err } = await supabase.from('user_education').insert([dbData]).select().single();
      if (err) throw err;
      setEducation(prev => [normalizeEducation(result), ...prev]);
      return { success: true, data: result };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'education' }) }; }
  };
  const updateEducation = async (id, data) => {
    try {
      await requireSession();
      const { data: result, error: err } = await supabase.from('user_education').update(denormalizeEducation(data)).eq('id', id).select().single();
      if (err) throw err;
      setEducation(prev => prev.map(e => e.id === id ? normalizeEducation(result) : e));
      return { success: true, data: result };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'education' }) }; }
  };
  const deleteEducation = async (id) => {
    try {
      await requireSession();
      const { error: err } = await supabase.from('user_education').delete().eq('id', id);
      if (err) throw err;
      setEducation(prev => prev.filter(e => e.id !== id));
      return { success: true };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'education' }) }; }
  };

  // ── EMPLOYER CRUD ───────────────────────────────────────────────────
  const createEmployer = async (data) => {
    try {
      await requireSession();
      const dbData = { ...denormalizeEmployer(data), user_id: userId };
      const { data: result, error: err } = await supabase.from('user_employers').insert([dbData]).select().single();
      if (err) throw err;
      setEmployers(prev => [normalizeEmployer(result), ...prev]);
      return { success: true, data: result };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'employer' }) }; }
  };
  const updateEmployer = async (id, data) => {
    try {
      await requireSession();
      const { data: result, error: err } = await supabase.from('user_employers').update(denormalizeEmployer(data)).eq('id', id).select().single();
      if (err) throw err;
      setEmployers(prev => prev.map(e => e.id === id ? normalizeEmployer(result) : e));
      return { success: true, data: result };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'employer' }) }; }
  };
  const deleteEmployer = async (id) => {
    try {
      await requireSession();
      const { error: err } = await supabase.from('user_employers').delete().eq('id', id);
      if (err) throw err;
      setEmployers(prev => prev.filter(e => e.id !== id));
      return { success: true };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'employer' }) }; }
  };

  // ── PERFORMANCE REVIEW CRUD (fix #8) ────────────────────────────────
  const createPerformanceReview = async (data) => {
    try {
      await requireSession();
      const dbData = { ...denormalizePerformanceReview(data), user_id: userId };
      const { data: result, error: err } = await supabase.from('user_performance_reviews').insert([dbData]).select().single();
      if (err) throw err;
      const normalized = normalizePerformanceReview(result);
      setPerformanceReviews(prev => [normalized, ...prev]);
      return { success: true, data: normalized };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'performance review' }) }; }
  };
  const updatePerformanceReview = async (id, data) => {
    try {
      await requireSession();
      const { data: result, error: err } = await supabase.from('user_performance_reviews').update(denormalizePerformanceReview(data)).eq('id', id).select().single();
      if (err) throw err;
      setPerformanceReviews(prev => prev.map(r => r.id === id ? normalizePerformanceReview(result) : r));
      return { success: true, data: result };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'performance review' }) }; }
  };
  const deletePerformanceReview = async (id) => {
    try {
      await requireSession();
      const { error: err } = await supabase.from('user_performance_reviews').delete().eq('id', id);
      if (err) throw err;
      setPerformanceReviews(prev => prev.filter(r => r.id !== id));
      // Also remove local criteria/agreements for this review
      setReviewCriteria(prev => prev.filter(c => c.reviewId !== id));
      setReviewAgreements(prev => prev.filter(a => a.reviewId !== id));
      return { success: true };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'performance review' }) }; }
  };

  // ── REVIEW CRITERIA CRUD (Section B) ─────────────────────────────────
  const [reviewCriteria, setReviewCriteria] = useState([]);
  const [reviewAgreements, setReviewAgreements] = useState([]);

  const getCriteriaForReview = useCallback((reviewId) => {
    return reviewCriteria.filter(c => c.reviewId === reviewId).sort((a, b) => a.sortOrder - b.sortOrder);
  }, [reviewCriteria]);

  const upsertCriterion = async (reviewId, criterion) => {
    try {
      await requireSession();
      const dbData = {
        review_id: reviewId,
        criterion_name: criterion.criterionName,
        score: criterion.score ?? null,
        strengths_evidence: criterion.strengthsEvidence || null,
        improvement_notes: criterion.improvementNotes || null,
        is_custom: criterion.isCustom || false,
        sort_order: criterion.sortOrder ?? 0,
      };
      if (criterion.id) {
        // Update
        const { data: result, error: err } = await supabase
          .from('user_review_criteria').update(dbData).eq('id', criterion.id).select().single();
        if (err) throw err;
        const norm = normalizeCriterion(result);
        setReviewCriteria(prev => prev.map(c => c.id === criterion.id ? norm : c));
      } else {
        // Insert
        const { data: result, error: err } = await supabase
          .from('user_review_criteria').insert([dbData]).select().single();
        if (err) throw err;
        setReviewCriteria(prev => [...prev, normalizeCriterion(result)]);
      }
      return { success: true };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'save', entity: 'criterion' }) }; }
  };

  const deleteCriterion = async (criterionId) => {
    try {
      await requireSession();
      const { error: err } = await supabase.from('user_review_criteria').delete().eq('id', criterionId);
      if (err) throw err;
      setReviewCriteria(prev => prev.filter(c => c.id !== criterionId));
      return { success: true };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'criterion' }) }; }
  };

  // ── REVIEW AGREEMENTS CRUD (Section D) ───────────────────────────────
  const getAgreementsForReview = useCallback((reviewId) => {
    return reviewAgreements.filter(a => a.reviewId === reviewId).sort((a, b) => a.sortOrder - b.sortOrder);
  }, [reviewAgreements]);

  const upsertAgreement = async (reviewId, agreement) => {
    try {
      await requireSession();
      const dbData = {
        review_id: reviewId,
        agreement_text: agreement.agreementText || null,
        owner: agreement.owner || null,
        start_date: agreement.startDate || null,
        due_date: agreement.dueDate || null,
        success_measure: agreement.successMeasure || null,
        check_in_date: agreement.checkInDate || null,
        status: agreement.status || 'Not started',
        notes: agreement.notes || null,
        sort_order: agreement.sortOrder ?? 0,
      };
      if (agreement.id) {
        const { data: result, error: err } = await supabase
          .from('user_review_agreements').update(dbData).eq('id', agreement.id).select().single();
        if (err) throw err;
        const norm = normalizeAgreement(result);
        setReviewAgreements(prev => prev.map(a => a.id === agreement.id ? norm : a));
      } else {
        const { data: result, error: err } = await supabase
          .from('user_review_agreements').insert([dbData]).select().single();
        if (err) throw err;
        setReviewAgreements(prev => [...prev, normalizeAgreement(result)]);
      }
      return { success: true };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'save', entity: 'agreement' }) }; }
  };

  const deleteAgreement = async (agreementId) => {
    try {
      await requireSession();
      const { error: err } = await supabase.from('user_review_agreements').delete().eq('id', agreementId);
      if (err) throw err;
      setReviewAgreements(prev => prev.filter(a => a.id !== agreementId));
      return { success: true };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'agreement' }) }; }
  };

  // ── EVALUATION REPORT CRUD (fix #9) ─────────────────────────────────
  const createEvaluationReport = async (data) => {
    try {
      await requireSession();
      const dbData = { ...denormalizeEvaluationReport(data), user_id: userId };
      const { data: result, error: err } = await supabase.from('user_evaluation_reports').insert([dbData]).select().single();
      if (err) throw err;
      const normalized = normalizeEvaluationReport(result);
      setEvaluationReports(prev => [normalized, ...prev]);
      return { success: true, data: normalized };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'evaluation report' }) }; }
  };
  const updateEvaluationReport = async (id, data) => {
    try {
      await requireSession();
      const { data: result, error: err } = await supabase.from('user_evaluation_reports').update(denormalizeEvaluationReport(data)).eq('id', id).select().single();
      if (err) throw err;
      setEvaluationReports(prev => prev.map(r => r.id === id ? normalizeEvaluationReport(result) : r));
      return { success: true, data: result };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'evaluation report' }) }; }
  };
  const deleteEvaluationReport = async (id) => {
    try {
      await requireSession();
      const { error: err } = await supabase.from('user_evaluation_reports').delete().eq('id', id);
      if (err) throw err;
      setEvaluationReports(prev => prev.filter(r => r.id !== id));
      return { success: true };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'evaluation report' }) }; }
  };

  // ── NOTES CRUD (v6) ────────────────────────────────────────────────
  // Table: public.user_notes (id, user_id, note_type, note, created_at)
  const createNote = async (data) => {
    try {
      await requireSession();
      const dbData = {
        user_id: userId,
        note_type: data.noteType || 'General',
        note: data.note || '',
      };
      if (data.createdAt) dbData.created_at = data.createdAt;
      const { data: result, error: err } = await supabase
        .from('user_notes').insert([dbData]).select().single();
      // If insert fails with 401/403/42501, check user_notes RLS policies.
      if (err) throw err;
      const normalized = normalizeNote(result);
      setNotes(prev => [normalized, ...prev]);
      return { success: true, data: normalized };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'user note' }) }; }
  };
  const updateNote = async (id, data) => {
    try {
      await requireSession();
      const dbData = {};
      if (data.noteType !== undefined) dbData.note_type = data.noteType;
      if (data.note !== undefined) dbData.note = data.note;
      if (data.createdAt !== undefined) dbData.created_at = data.createdAt;
      const { data: result, error: err } = await supabase
        .from('user_notes').update(dbData).eq('id', id).select().single();
      if (err) throw err;
      setNotes(prev => prev.map(n => n.id === id ? normalizeNote(result) : n));
      return { success: true, data: normalizeNote(result) };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'user note' }) }; }
  };
  const deleteNote = async (id) => {
    try {
      await requireSession();
      const { error: err } = await supabase.from('user_notes').delete().eq('id', id);
      if (err) throw err;
      setNotes(prev => prev.filter(n => n.id !== id));
      return { success: true };
    } catch (err) { return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'user note' }) }; }
  };

  return {
    // State
    profile, addresses, salaryHistory, education, employers,
    performanceReviews, evaluationReports, notes,
    loading, error,
    // Profile
    upsertProfile,
    // Addresses
    createAddress, updateAddress, deleteAddress, searchAddressDirectory,
    // Salary (read-only stubs)
    createSalaryEntry, updateSalaryEntry, deleteSalaryEntry,
    // Education
    createEducation, updateEducation, deleteEducation,
    // Employers
    createEmployer, updateEmployer, deleteEmployer,
    // Performance Reviews
    createPerformanceReview, updatePerformanceReview, deletePerformanceReview,
    // Review Criteria (Section B)
    getCriteriaForReview, upsertCriterion, deleteCriterion,
    // Review Agreements (Section D)
    getAgreementsForReview, upsertAgreement, deleteAgreement,
    // Evaluation Reports
    createEvaluationReport, updateEvaluationReport, deleteEvaluationReport,
    // Notes (v6)
    createNote, updateNote, deleteNote,
    // Refetch
    refetch: fetchAll,
  };
}

// ============================================================================
// STANDALONE POST-HIRE HELPERS (v5)
// ============================================================================
// These are exported as standalone async functions (not hook methods) so they
// can be called from useSupabaseHiring.hireCandidate() without instantiating
// the profile hook. They write directly via Supabase and do NOT update any
// React state — the TeamMemberFullScreenPage will fetch fresh data when opened.
//
// All three follow the same pattern:
//   1. Validate input
//   2. Delete existing rows for user_id (replace semantics)
//   3. Insert new rows
//   4. Return { success, error? }
//
// ── SQL PREREQUISITES (already exist if hiring page works) ──────────
// Tables: user_education, user_employers, user_addresses
// All have user_id UUID column referencing public.users.
// ════════════════════════════════════════════════════════════════════

/**
 * replaceEducationForUser — delete existing education rows and insert new ones
 * @param {string} userId — UUID of the new user
 * @param {Array} degrees — array of { type, institution, graduationDate, duration, notes }
 */
export async function replaceEducationForUser(userId, degrees = []) {
  try {
    await requireSession();
    if (!userId) throw new Error('replaceEducationForUser: userId is required');

    // Delete existing education rows for this user
    const { error: delErr } = await supabase
      .from('user_education')
      .delete()
      .eq('user_id', userId);
    if (delErr) {
      debugLog('replaceEducation delete error:', delErr);
      // Continue — might be zero rows
    }

    // Filter to rows with at least some content
    const validDegrees = (degrees || []).filter(d => d.type || d.institution);
    if (validDegrees.length === 0) return { success: true };

    const rows = validDegrees.map((d, idx) => ({
      user_id: userId,
      type: d.type || null,
      institution: d.institution || null,
      graduation_date: d.graduationDate || null,
      duration: d.duration || null,
      notes: d.notes || null,
      // sort_index column (optional — table may not have it)
      ...(idx !== undefined ? {} : {}),
    }));

    const { error: insErr } = await supabase
      .from('user_education')
      .insert(rows);
    if (insErr) throw insErr;

    debugLog('replaceEducation: inserted', rows.length, 'rows for user', userId);
    return { success: true };
  } catch (err) {
    console.error('[useTeamMemberProfile] replaceEducationForUser error:', err);
    return { success: false, error: formatSupabaseError(err, { action: 'replace', entity: 'education' }) };
  }
}

/**
 * replaceEmployersForUser — delete existing employer rows and insert new ones
 * @param {string} userId — UUID of the new user
 * @param {Array} pastEmployment — array of strings OR objects { name/employer, role, startDate, endDate, notes }
 */
export async function replaceEmployersForUser(userId, pastEmployment = []) {
  try {
    await requireSession();
    if (!userId) throw new Error('replaceEmployersForUser: userId is required');

    // Delete existing employer rows for this user
    const { error: delErr } = await supabase
      .from('user_employers')
      .delete()
      .eq('user_id', userId);
    if (delErr) {
      debugLog('replaceEmployers delete error:', delErr);
    }

    // Normalize: pastEmployment can be array of strings or objects
    const items = (pastEmployment || []).filter(Boolean);
    if (items.length === 0) return { success: true };

    const rows = items.map((item) => {
      // String form: just employer name
      if (typeof item === 'string') {
        return {
          user_id: userId,
          name: item,
        };
      }
      // Object form: { name, employer, company, role, startDate, endDate, notes }
      return {
        user_id: userId,
        name: item.name || item.employer || item.company || 'Unknown',
        role: item.role || null,
        start_date: item.startDate || null,
        end_date: item.endDate || null,
        notes: item.notes || null,
      };
    });

    const { error: insErr } = await supabase
      .from('user_employers')
      .insert(rows);
    if (insErr) throw insErr;

    debugLog('replaceEmployers: inserted', rows.length, 'rows for user', userId);
    return { success: true };
  } catch (err) {
    console.error('[useTeamMemberProfile] replaceEmployersForUser error:', err);
    return { success: false, error: formatSupabaseError(err, { action: 'replace', entity: 'employer' }) };
  }
}

/**
 * upsertPrimaryHomeAddressForUser — create or update the "Home" address
 * @param {string} userId — UUID of the new user
 * @param {Object} address — { line1, line2, city, state, postalCode, country }
 *   Also accepts hiring-format keys: { addressLine1, addressCity, … }
 */
export async function upsertPrimaryHomeAddressForUser(userId, address = {}) {
  try {
    await requireSession();
    if (!userId) throw new Error('upsertPrimaryHomeAddressForUser: userId is required');

    // Normalize field names (accept both camelCase hiring format and short form)
    const line1 = address.line1 || address.addressLine1 || '';
    const line2 = address.line2 || address.addressLine2 || '';
    const city = address.city || address.addressCity || '';
    const state = address.state || address.addressState || '';
    const postalCode = address.postalCode || address.addressPostalCode || '';
    const country = address.country || address.addressCountry || '';

    // Skip if address is empty
    const hasContent = line1 || city || country;
    if (!hasContent) {
      debugLog('upsertPrimaryHomeAddress: skipped — no address content');
      return { success: true };
    }

    // Check if a "Home" address already exists for this user
    const { data: existing } = await supabase
      .from('user_addresses')
      .select('id')
      .eq('user_id', userId)
      .eq('label', 'Home')
      .maybeSingle();

    const addrData = {
      user_id: userId,
      label: 'Home',
      line1: line1 || null,
      line2: line2 || null,
      city: city || null,
      state: state || null,
      postal_code: postalCode || null,
      country: country || null,
    };

    if (existing?.id) {
      // Update existing "Home" address
      const { error: upErr } = await supabase
        .from('user_addresses')
        .update(addrData)
        .eq('id', existing.id);
      if (upErr) throw upErr;
      debugLog('upsertPrimaryHomeAddress: updated existing Home address for user', userId);
    } else {
      // Insert new "Home" address
      const { error: insErr } = await supabase
        .from('user_addresses')
        .insert([addrData]);
      if (insErr) throw insErr;
      debugLog('upsertPrimaryHomeAddress: inserted new Home address for user', userId);
    }

    return { success: true };
  } catch (err) {
    console.error('[useTeamMemberProfile] upsertPrimaryHomeAddressForUser error:', err);
    return { success: false, error: formatSupabaseError(err, { action: 'upsert', entity: 'home address' }) };
  }
}

// ============================================================================
// STANDALONE POST-HIRE HELPERS (v6) — User Notes
// ============================================================================
// Standalone async functions for use from useSupabaseHiring.hireCandidate().
// Same pattern as the v5 helpers above: no React state, direct Supabase writes.
//
// Table: public.user_notes (
//   id            uuid default gen_random_uuid() primary key,
//   user_id       bigint references public.users(id) not null,
//   note_type     text not null,
//   note          text not null,
//   created_at    timestamptz default now() not null
// )
// ============================================================================

// ── LOCAL STORAGE FALLBACK ────────────────────────────────────────────
const LS_USER_NOTES_KEY = 'tt_user_notes_v1';

const loadLSNotes = () => {
  try {
    const stored = localStorage.getItem(LS_USER_NOTES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch { return []; }
};

const saveLSNotes = (notes) => {
  try { localStorage.setItem(LS_USER_NOTES_KEY, JSON.stringify(notes)); }
  catch (e) { console.error('[UserNotes] LS save error:', e); }
};

/**
 * addUserNote — Insert a note into public.user_notes (Supabase) or localStorage.
 * Standalone function — does NOT update React state.
 *
 * @param {object} params
 * @param {number} params.userId      — BIGINT public.users.id (NOT auth UUID)
 * @param {string} params.noteType    — e.g. 'Hiring Interview'
 * @param {string} params.note        — formatted note text
 * @param {string} [params.createdAt] — ISO timestamp; defaults to DB now()
 * @param {boolean} [useSupabaseDB=true] — false → localStorage fallback
 * @returns {{ success: boolean, data?: object, error?: string }}
 */
export async function addUserNote({ userId, noteType, note, createdAt }, useSupabaseDB = true) {
  if (!userId) return { success: false, error: 'addUserNote: userId is required' };
  if (!noteType) return { success: false, error: 'addUserNote: noteType is required' };
  if (!note) return { success: false, error: 'addUserNote: note text is required' };

  if (useSupabaseDB) {
    try {
      await requireSession();
      const insertData = {
        user_id: userId,        // BIGINT — public.users.id
        note_type: noteType,
        note: note,
      };
      if (createdAt) insertData.created_at = createdAt;

      const { data, error } = await supabase
        .from('user_notes')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        // ── RLS HINT ────────────────────────────────────────────────
        // If this fails with 401/403/42501, check user_notes RLS policies.
        // ────────────────────────────────────────────────────────────
        console.error('[UserNotes] Insert error:', error);
        return { success: false, error: formatSupabaseError(error, { action: 'create', entity: 'user note' }) };
      }

      return { success: true, data: normalizeNote(data) };
    } catch (err) {
      console.error('[UserNotes] addUserNote error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'user note' }) };
    }
  }

  // localStorage fallback
  const notes = loadLSNotes();
  const newNote = {
    id: crypto.randomUUID(),
    userId, noteType, note,
    createdAt: createdAt || new Date().toISOString(),
  };
  notes.unshift(newNote);
  saveLSNotes(notes);
  return { success: true, data: newNote };
}

/**
 * fetchUserNotes — Retrieve all notes for a user, ordered by created_at desc.
 * Standalone function — does NOT update React state.
 *
 * @param {number} userId — BIGINT public.users.id
 * @param {boolean} [useSupabaseDB=true]
 * @returns {{ success: boolean, data?: Array, error?: string }}
 */
export async function fetchUserNotes(userId, useSupabaseDB = true) {
  if (!userId) return { success: false, error: 'fetchUserNotes: userId is required', data: [] };

  if (useSupabaseDB) {
    try {
      const { data, error } = await supabase
        .from('user_notes')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[UserNotes] Fetch error:', error);
        return { success: false, error: formatSupabaseError(error, { action: 'fetch', entity: 'user notes' }), data: [] };
      }
      return { success: true, data: (data || []).map(normalizeNote) };
    } catch (err) {
      console.error('[UserNotes] fetchUserNotes error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'fetch', entity: 'user notes' }), data: [] };
    }
  }

  // localStorage fallback
  const notes = loadLSNotes().filter(n => String(n.userId) === String(userId));
  notes.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  return { success: true, data: notes };
}

/**
 * composeHiringInterviewNote — Build formatted note text from interview data.
 * Pure helper (no DB access). Called by hireCandidate to compose the note
 * text before passing to addUserNote.
 *
 * @param {object} interview — The latest interview object (camelCase fields), or null
 * @param {Array}  activeUsers — Users list to resolve interviewTakenBy UUID → name
 * @param {string} [candidateName] — Candidate's display name
 * @returns {string} Formatted note text
 */
export function composeHiringInterviewNote(interview, activeUsers = [], candidateName = '') {
  // No interview data at all — return a stub note
  if (!interview) {
    return candidateName
      ? `Candidate: ${candidateName}\n\nNo interview notes available.`
      : 'No interview notes available.';
  }

  const lines = [];

  // Candidate name (always first if available)
  if (candidateName) {
    lines.push(`Candidate: ${candidateName}`);
  }

  // Interview date
  lines.push(`Interview Date: ${interview.interviewDate || 'Unknown'}`);

  // Interviewer name resolution (interviewTakenBy is UUID, resolve to name)
  let interviewerName = '';
  if (interview.interviewTakenBy) {
    const interviewer = activeUsers.find(u => {
      const ids = [u?.authUserId, u?.auth_user_id, u?.user_id, u?.profileId, u?.profile_id, u?.id];
      return ids.some(c => String(c) === String(interview.interviewTakenBy));
    });
    interviewerName = interviewer?.name || interview.interviewTakenBy;
  }
  if (interviewerName) {
    lines.push(`Interview Taken By: ${interviewerName}`);
  }

  // Recommended
  lines.push(`Recommended: ${interview.recommended ? 'Yes' : 'No'}`);

  // Overall Comments
  if (interview.overallComments) {
    lines.push('');
    lines.push('Overall Comments:');
    lines.push(interview.overallComments);
  }

  // Final Verdict Notes
  if (interview.finalVerdictNotes) {
    lines.push('');
    lines.push('Final Verdict Notes:');
    lines.push(interview.finalVerdictNotes);
  }

  // Optional financial / plans info
  const extras = [];
  if (interview.salaryExpectationsMonthly != null) {
    extras.push(`Salary Expectations (monthly): ${interview.salaryExpectationsMonthly}`);
  }
  if (interview.currentSalaryMonthly != null) {
    extras.push(`Current Salary (monthly): ${interview.currentSalaryMonthly}`);
  }
  if (interview.futurePlans) {
    extras.push(`Future Plans: ${interview.futurePlans}`);
  }
  if (extras.length > 0) {
    lines.push('');
    extras.forEach(e => lines.push(e));
  }

  return lines.join('\n');
}
